import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { ThemeToggle } from "@/components/theme-toggle";
import { CheckCircle2, Search } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

type StatusResponse = {
  confirmationCode: string;
  status: "received" | "in_progress" | "completed" | "rejected";
  source: string;
  platform: string | null;
  createdAt: string;
  completedAt: string | null;
  rejectedReason: string | null;
};

export default function DataDeletionPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState<{ confirmationCode: string } | null>(null);
  const [lookupCode, setLookupCode] = useState("");
  const [activeLookup, setActiveLookup] = useState<string | null>(null);

  const [form, setForm] = useState({
    requesterEmail: "",
    platform: "all" as "all" | "facebook" | "instagram" | "tiktok" | "youtube",
    requesterNotes: "",
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const platformValue = form.platform === "all" ? null : form.platform;
      const res = await apiRequest("POST", "/api/data-deletion/request", {
        requesterEmail: form.requesterEmail,
        platform: platformValue,
        requesterNotes: form.requesterNotes,
      });
      return res.json() as Promise<{ confirmationCode: string }>;
    },
    onSuccess: (data) => {
      setSubmitted(data);
      toast({ title: "Request received", description: `Your confirmation code is ${data.confirmationCode}` });
      queryClient.invalidateQueries({ queryKey: ["/api/data-deletion/status"] });
    },
    onError: (e: Error) => {
      toast({ title: "Could not submit request", description: e.message, variant: "destructive" });
    },
  });

  const { data: lookupData, isLoading: lookupLoading } = useQuery<StatusResponse>({
    queryKey: ["/api/data-deletion/status", activeLookup],
    queryFn: async () => {
      const res = await fetch(`/api/data-deletion/status/${activeLookup}`);
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!activeLookup,
  });

  return (
    <div className="min-h-screen bg-background" data-testid="page-data-deletion">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          <a href="/" className="font-semibold text-lg" data-testid="link-home">Clarik</a>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2" data-testid="text-page-title">
            Data Deletion Request
          </h1>
          <p className="text-muted-foreground">
            Request that Clarik delete data we hold about you, either across the entire
            platform or only the data we received from a specific connected social media platform
            (Meta, TikTok, or YouTube).
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Submit a request</CardTitle>
            <CardDescription>
              We will confirm receipt immediately and process your request within 30 days.
              You will receive a confirmation code below that you can use to check status at any time.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submitted ? (
              <Alert className="border-green-500/40 bg-green-50 dark:bg-green-950/20" data-testid="alert-submitted">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertTitle className="text-green-900 dark:text-green-100">Request received</AlertTitle>
                <AlertDescription className="text-green-800 dark:text-green-200/80">
                  Your confirmation code is{" "}
                  <code className="font-mono font-semibold" data-testid="text-confirmation-code">
                    {submitted.confirmationCode}
                  </code>
                  . Save this code — you can use it at any time on this page to check the status
                  of your request.
                </AlertDescription>
              </Alert>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitMutation.mutate();
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="requesterEmail">Your email</Label>
                  <Input
                    id="requesterEmail"
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={form.requesterEmail}
                    onChange={(e) => setForm({ ...form, requesterEmail: e.target.value })}
                    data-testid="input-requester-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="platform">What should we delete?</Label>
                  <Select
                    value={form.platform}
                    onValueChange={(v) => setForm({ ...form, platform: v as typeof form.platform })}
                  >
                    <SelectTrigger id="platform" data-testid="select-platform">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Everything we have about me</SelectItem>
                      <SelectItem value="facebook">Only my Facebook data</SelectItem>
                      <SelectItem value="instagram">Only my Instagram data</SelectItem>
                      <SelectItem value="tiktok">Only my TikTok data</SelectItem>
                      <SelectItem value="youtube">Only my YouTube data</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="requesterNotes">Additional details (optional)</Label>
                  <Textarea
                    id="requesterNotes"
                    rows={3}
                    placeholder="Anything that helps us locate your data (account username, workspace name, etc.)"
                    value={form.requesterNotes}
                    onChange={(e) => setForm({ ...form, requesterNotes: e.target.value })}
                    data-testid="input-requester-notes"
                  />
                </div>

                <Button type="submit" disabled={submitMutation.isPending} data-testid="button-submit-request">
                  {submitMutation.isPending ? "Submitting..." : "Submit deletion request"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Check status of an existing request</CardTitle>
            <CardDescription>
              Paste the confirmation code we gave you to see the current status.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Confirmation code"
                value={lookupCode}
                onChange={(e) => setLookupCode(e.target.value.trim())}
                data-testid="input-lookup-code"
              />
              <Button
                variant="outline"
                onClick={() => setActiveLookup(lookupCode || null)}
                disabled={!lookupCode}
                data-testid="button-check-status"
              >
                <Search className="h-4 w-4 mr-2" />
                Check
              </Button>
            </div>

            {activeLookup && (
              <>
                {lookupLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : lookupData ? (
                  <div className="border rounded-md p-4 space-y-2 text-sm" data-testid="card-lookup-result">
                    <div><span className="text-muted-foreground">Code:</span> <code>{lookupData.confirmationCode}</code></div>
                    <div><span className="text-muted-foreground">Status:</span> <span className="font-medium">{lookupData.status}</span></div>
                    <div><span className="text-muted-foreground">Submitted:</span> {new Date(lookupData.createdAt).toLocaleString()}</div>
                    {lookupData.completedAt && (
                      <div><span className="text-muted-foreground">Completed:</span> {new Date(lookupData.completedAt).toLocaleString()}</div>
                    )}
                    {lookupData.rejectedReason && (
                      <div><span className="text-muted-foreground">Reason:</span> {lookupData.rejectedReason}</div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground" data-testid="text-lookup-not-found">
                    No request found with that code.
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          For Meta-initiated deletion callbacks (when a user removes the Clarik app from
          their Facebook account), the platform automatically POSTs to our deletion-callback
          endpoint and we begin processing without you needing to fill out this form. The
          confirmation code returned to Meta is the same code shown here. See our{" "}
          <a href="/privacy" className="text-primary hover:underline" data-testid="link-privacy">Privacy Policy</a>{" "}
          for details.
        </p>
      </main>
    </div>
  );
}
